import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  BsSliders,
  BsExclamationTriangle,
  BsPlusCircle,
  BsLightning,
} from "react-icons/bs";
import AttributeFilterParser from "../../../utils/attributeFilterParser";
import LoadingSpinner from "../../common/LoadingSpinner";

const AttributeFilterInput = ({
  gridRef,
  showBuilder,
  addFilter,
  filterString = "",
  setFilterString,
}) => {
  const [inputValue, setInputValue] = useState(filterString);
  const [validation, setValidation] = useState({ valid: true });
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [enableOptimization, setEnableOptimization] = useState(false);
  const textareaRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Initialize parser
  const parser = useMemo(() => new AttributeFilterParser(gridRef), [gridRef]);

  // Update input when external filter string changes
  useEffect(() => {
    if (filterString !== inputValue) {
      setInputValue(filterString);
    }
  }, [filterString, inputValue]);

  // Real-time validation
  const validateInput = useCallback(
    (value) => {
      if (!value.trim()) {
        return { valid: true };
      }
      return parser.validate(value);
    },
    [parser]
  );

  // Common function to update input value with validation
  const updateInputValue = useCallback(
    (value) => {
      setInputValue(value);

      // Validate and update external state
      const validationResult = validateInput(value);
      setValidation(validationResult);

      if (setFilterString) {
        setFilterString(value);
      }
    },
    [validateInput, setFilterString]
  );

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      updateInputValue(value);
      setCursorPosition(e.target.selectionStart);

      // Show autocomplete on typing
      if (value.length > 0 && !showAutocomplete) {
        setShowAutocomplete(true);
      }
    },
    [updateInputValue, showAutocomplete]
  );

  const handleKeyDown = useCallback(
    (e) => {
      setCursorPosition(e.target.selectionStart);

      if (e.key === "Tab" || e.key === "Enter") {
        if (showAutocomplete) {
          e.preventDefault();
          setShowAutocomplete(false);
        }
      } else if (e.key === "Escape") {
        setShowAutocomplete(false);
      }
    },
    [showAutocomplete]
  );

  const handleFocus = useCallback(() => {
    if (inputValue.trim().length > 0) {
      setShowAutocomplete(true);
    }
  }, [inputValue]);

  const handleBlur = useCallback((e) => {
    // Delay hiding to allow click on autocomplete items
    setTimeout(() => {
      if (!autocompleteRef.current?.contains(document.activeElement)) {
        setShowAutocomplete(false);
      }
    }, 150);
  }, []);

  const [isApplying, setIsApplying] = useState(false);

  const applyFilter = useCallback(async () => {
    if (!inputValue.trim()) return;

    const validationResult = validateInput(inputValue);
    if (!validationResult.valid) {
      setValidation(validationResult);
      return;
    }

    setIsApplying(true);
    try {
      // Parse to ensure it's valid
      parser.parse(inputValue);

      // Always send structured object with optimization flag
      const filterValue = {
        query: inputValue,
        optimize: enableOptimization,
      };

      // Apply the filter
      await addFilter("attribute_filter", filterValue, "Attribute Filter");
    } catch (error) {
      setValidation({
        valid: false,
        error: error.message,
      });
    } finally {
      setIsApplying(false);
    }
  }, [
    inputValue,
    validateInput,
    parser,
    addFilter,
    enableOptimization,
  ]);

  const openBuilder = useCallback(() => {
    if (showBuilder) {
      showBuilder();
    }
  }, [showBuilder]);

  // Get autocomplete suggestions
  const getAutocompleteSuggestions = useCallback(() => {
    const columns = parser.getAvailableColumns();
    const operators = ["=", "<", ">", "<=", ">=", "AND", "OR"];

    // Get current word at cursor (split on whitespace and punctuation)
    const beforeCursor = inputValue.substring(0, cursorPosition);
    const words = beforeCursor.split(/[\s()=<>!]+/);
    const currentWord = words[words.length - 1] || "";

    let suggestions = [];

    // Only show suggestions if we have something to filter on
    if (currentWord.length === 0) return [];

    // Suggest columns first
    const columnSuggestions = columns
      .filter(
        (col) =>
          col.value.toLowerCase().startsWith(currentWord.toLowerCase()) ||
          col.headerName.toLowerCase().includes(currentWord.toLowerCase()) ||
          col.displayName.toLowerCase().includes(currentWord.toLowerCase())
      )
      .slice(0, 5);

    // Add operator suggestions if they match
    const operatorSuggestions = operators
      .filter((op) => op.toLowerCase().startsWith(currentWord.toLowerCase()))
      .map((op) => ({ label: op, value: op, type: "operator" }))
      .slice(0, 3);

    suggestions = [...columnSuggestions, ...operatorSuggestions];

    return suggestions.slice(0, 8);
  }, [inputValue, cursorPosition, parser]);

  const insertSuggestion = useCallback(
    (suggestion) => {
      const beforeCursor = inputValue.substring(0, cursorPosition);
      const afterCursor = inputValue.substring(cursorPosition);
      const words = beforeCursor.split(/[\s()=<>!]+/);
      const currentWord = words[words.length - 1] || "";

      // Replace only the current word, not the entire text before cursor
      const beforeWord = beforeCursor.substring(
        0,
        beforeCursor.length - currentWord.length
      );
      const newValue = beforeWord + suggestion.value + " " + afterCursor;

      updateInputValue(newValue);
      setShowAutocomplete(false);

      // Focus back to textarea
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    },
    [inputValue, cursorPosition, updateInputValue]
  );

  const insertExample = useCallback(
    (example) => {
      updateInputValue(example);

      // Focus back to textarea
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    },
    [updateInputValue]
  );

  const toggleExamples = useCallback(() => {
    setShowExamples((prev) => !prev);
    setShowAutocomplete(false);
  }, []);

  const suggestions = showAutocomplete ? getAutocompleteSuggestions() : [];

  const renderAutocomplete = () => {
    if (!showAutocomplete || suggestions.length === 0) return null;

    return (
      <ul
        ref={autocompleteRef}
        className="dropdown-menu show position-absolute"
        style={{
          top: "100%",
          left: 0,
          right: 0,
          zIndex: 1050,
          maxHeight: "120px",
          overflowY: "auto",
          fontSize: "0.85rem",
          marginTop: "2px",
          minWidth: "auto",
        }}
      >
        {suggestions.map((suggestion, index) => (
          <li key={`${suggestion.value}-${index}`}>
            <button
              type="button"
              className="dropdown-item d-flex justify-content-between align-items-center py-1"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertSuggestion(suggestion)}
              style={{ fontSize: "0.85rem" }}
            >
              <span>{suggestion.displayName || suggestion.value}</span>
              <small
                className="text-muted ms-2"
                style={{ fontSize: "0.75rem" }}
              >
                {suggestion.type === "operator" ? "op" : suggestion.type}
              </small>
            </button>
          </li>
        ))}
      </ul>
    );
  };

  const renderExamples = () => {
    if (!showExamples) return null;

    const examples = [
      {
        title: "Simple comparison",
        example: "AF >= 0.5",
        description: "Allele frequency greater than or equal to 0.5",
      },
      {
        title: "Text matching",
        example: "REF = 'C'",
        description: "Reference allele equals C (quotes required for text)",
      },
      {
        title: "Multiple conditions",
        example: "(REF = 'C' OR REF = 'T') AND AF >= 0.3",
        description: "Combine conditions with AND/OR logic",
      },
      {
        title: "Range syntax",
        example: "0.3 <= AF <= 0.7",
        description: "Shorthand for value between two numbers",
      },
    ];

    return (
      <div className="bg-light border border-secondary border-opacity-25 rounded-3 p-2 p-sm-3 mt-3">
        <div className="text-muted small fw-semibold mb-2">
          Examples - Click to use:
        </div>

        {examples.map((item, index) => (
          <div key={index} className="mb-2">
            <div className="text-muted small mb-1">{item.title}</div>
            <div
              className="font-monospace text-body small p-2 bg-white rounded"
              onClick={() => insertExample(item.example)}
              style={{
                cursor: "pointer",
                fontSize: "0.85rem",
                lineHeight: "1.4",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {item.example}
            </div>
          </div>
        ))}

        <div className="mt-3 pt-2 border-top border-secondary border-opacity-25">
          <div className="text-muted small">
            <div>
              <strong>Operators:</strong> = &lt; &gt; &lt;= &gt;=
            </div>
            <div>
              <strong>Logic:</strong> AND OR ( )
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="attribute-filter-input">
      {/* Filter Expression Input */}
      <div className="position-relative">
        <div className="mb-2">
          <div className="position-relative">
            <div className="position-relative">
              <textarea
                ref={textareaRef}
                className={`form-control ${
                  !validation.valid ? "is-invalid" : ""
                }`}
                placeholder="Enter filter expression, e.g.: (REF = 'C' OR REF = 'T') AND (0.3 <= AF <= 0.7)"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                style={{
                  fontSize: "0.875rem",
                  fontFamily:
                    'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                  minHeight: "80px",
                  lineHeight: "1.4",
                  resize: "vertical",
                }}
              />
            </div>
            {renderAutocomplete()}
          </div>

          {/* Validation Messages */}
          {!validation.valid && (
            <div className="invalid-feedback d-block">
              <BsExclamationTriangle className="me-1" />
              {validation.error}
              {validation.suggestion && (
                <div className="mt-1 text-info">
                  <small>💡 {validation.suggestion}</small>
                </div>
              )}
            </div>
          )}

          {validation.valid && inputValue.trim() && (
            <div className="form-text text-success">
              ✓ Valid filter expression
            </div>
          )}
        </div>

        {/* Helper Buttons */}
        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={openBuilder}
              title="Open visual filter builder"
            >
              <BsSliders className="me-1" />
              Builder
            </button>

            <button
              type="button"
              className={`btn btn-sm ${
                enableOptimization ? "btn-warning" : "btn-outline-secondary"
              }`}
              onClick={() => setEnableOptimization(!enableOptimization)}
              title={
                enableOptimization
                  ? "Optimization enabled - May alter complex filter logic"
                  : "Enable query optimization (experimental)"
              }
              style={{ padding: "0.25rem 0.5rem" }}
            >
              <BsLightning />
            </button>
          </div>

          <button
            type="button"
            className={`btn btn-outline-secondary btn-sm ${
              showExamples ? "active" : ""
            }`}
            onClick={toggleExamples}
            title="Show syntax examples and help"
            style={{ padding: "0.25rem 0.5rem" }}
          >
            ?
          </button>
        </div>
      </div>

      {/* Apply Button */}
      <div className="d-grid">
        <button
          className="btn btn-primary btn-sm btn-animate d-flex align-items-center justify-content-center gap-2"
          onClick={applyFilter}
          disabled={!validation.valid || !inputValue.trim() || isApplying}
        >
          {isApplying ? (
            <LoadingSpinner size="1em" />
          ) : (
            <>
              <BsPlusCircle />
              Apply Filter
            </>
          )}
        </button>
      </div>

      {/* Examples Section */}
      {renderExamples()}
    </div>
  );
};

export default AttributeFilterInput;
